<template>
  <v-chip
    small
    class="export-frequency-chip"
    :class="`export-frequency-chip--${normalizedFrequency}`"
    :aria-label="`Export frequency: ${frequency}`"
  >
    <v-icon
      left
      x-small
    >
      {{ icon }}
    </v-icon>
    {{ displayText }}
  </v-chip>
</template>

<script lang="ts">
import Vue from 'vue'

interface FrequencyConfig {
  icon: string
  displayText: string
}

const frequencyConfig: Record<string, FrequencyConfig> = {
  daily: {
    icon: 'mdi-calendar-today',
    displayText: 'Daily'
  },
  weekly: {
    icon: 'mdi-calendar-week',
    displayText: 'Weekly'
  },
  monthly: {
    icon: 'mdi-calendar-month',
    displayText: 'Monthly'
  },
  hourly: {
    icon: 'mdi-clock-fast',
    displayText: 'Hourly'
  }
}

export default Vue.extend({
  name: 'ExportFrequencyChip',

  props: {
    /** Export schedule frequency */
    frequency: {
      type: String,
      required: true
    }
  },

  computed: {
    normalizedFrequency(): string {
      return this.frequency.toLowerCase()
    },

    config(): FrequencyConfig {
      return frequencyConfig[this.normalizedFrequency] || {
        icon: 'mdi-calendar',
        displayText: this.frequency
      }
    },

    icon(): string {
      return this.config.icon
    },

    displayText(): string {
      return this.config.displayText
    }
  }
})
</script>

<style scoped>
.export-frequency-chip {
  font-weight: 500;
  font-size: var(--font-size-xs);
}

/* Override Vuetify's v-chip colours with subtle tag styling for secondary information */
.export-frequency-chip--daily,
.export-frequency-chip--weekly,
.export-frequency-chip--monthly,
.export-frequency-chip--hourly {
  /* Subtle light grey-blue background, almost white */
  background-color: hsl(210, 20%, 96%) !important;
  /* Dark steel blue text for strong contrast */
  color: hsl(210, 28%, 40%) !important;
  border: none;
}

.export-frequency-chip--daily >>> .v-icon,
.export-frequency-chip--weekly >>> .v-icon,
.export-frequency-chip--monthly >>> .v-icon,
.export-frequency-chip--hourly >>> .v-icon {
  /* Match text colour for icons */
  color: hsl(210, 28%, 40%) !important;
}

/* Dark mode: inverted - light background with dark text for contrast */
:global(.dark) .export-frequency-chip--daily,
:global(.dark) .export-frequency-chip--weekly,
:global(.dark) .export-frequency-chip--monthly,
:global(.dark) .export-frequency-chip--hourly {
  /* Light grey-blue background (inverted from light mode) */
  background-color: hsl(210, 25%, 88%) !important;
  /* Dark steel blue text for strong contrast */
  color: hsl(210, 28%, 30%) !important;
  border: none;
}

:global(.dark) .export-frequency-chip--daily >>> .v-icon,
:global(.dark) .export-frequency-chip--weekly >>> .v-icon,
:global(.dark) .export-frequency-chip--monthly >>> .v-icon,
:global(.dark) .export-frequency-chip--hourly >>> .v-icon {
  /* Match text colour for icons in dark mode */
  color: hsl(210, 28%, 30%) !important;
}
</style>
